<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2 pt-4'">
    <!-- Top-up Form Card -->
    <v-row>
      <v-col cols="12" md="6">
        <v-card outlined class="ordera-box-shadow">
          <v-card-title>
            <v-icon left color="primary">mdi-cash-plus</v-icon>
            {{ $t("add_credit") }}
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-form ref="form" v-model="valid" @submit.prevent="submitTopup">
              <v-row>
                <!-- Limit Selection -->
                <v-col cols="12">
                  <autocomplete
                    v-model="selectedLimit"
                    :items="limitOptions"
                    :label="$t('limit')"
                    item-text="text"
                    item-value="value"
                    :loading="limitsLoading"
                    :rules="[(v) => !!v || $t('required_field')]"
                    prepend-inner-icon="mdi-credit-card-outline"
                    return-object
                    outlined
                    dense
                    :hide-details="false"
                    :hint="selectedLimitHint"
                    persistent-hint
                  />
                </v-col>

                <!-- Users Selection -->
                <v-col cols="12" md="6">
                  <autocomplete
                    v-model="selectedUsers"
                    :items="users"
                    :label="$t('users')"
                    item-text="name"
                    item-value="id"
                    :rules="[(v) => (v && v.length > 0) || $t('required_field')]"
                    prepend-inner-icon="mdi-account-multiple"
                    outlined
                    dense
                    multiple
                    chips
                    small-chips
                    deletable-chips
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <autocomplete
                    v-model="toppedBy"
                    :items="toppedByOptions"
                    :label="$t('topped_by')"
                    item-text="label"
                    item-value="value"
                    :rules="[(v) => !!v || $t('required_field')]"
                    prepend-inner-icon="mdi-account-check"
                    outlined
                    dense
                  />
                </v-col>

                <!-- Amount Input -->
                <v-col cols="12" md="6">
                  <text-field
                    v-model="topupAmount"
                    :label="$t('amount')"
                    type="number"
                    :rules="amountRules"
                    prepend-inner-icon="mdi-cash"
                    :suffix="restaurantCurrency"
                    outlined
                    dense
                  />
                </v-col>

                <!-- Payment Method -->
                <v-col cols="12" md="6">
                  <autocomplete
                    v-model="paymentMethod"
                    :items="paymentMethods"
                    :label="$t('payment_method')"
                    item-text="label"
                    item-value="value"
                    :rules="[(v) => !!v || $t('required_field')]"
                    prepend-inner-icon="mdi-credit-card"
                    outlined
                    dense
                  />
                </v-col>

                <!-- Quick Amount Buttons -->
                <v-col cols="12">
                  <div class="text-caption font-weight-medium">
                    {{ $t("quick_amounts") }}
                  </div>
                  <v-chip-group v-model="selectedQuickAmount" mandatory>
                    <v-chip
                      v-for="amount in quickAmounts"
                      :key="amount"
                      :value="amount"
                      color="primary"
                      @click="setQuickAmount(amount)"
                    >
                      {{ amount }} {{ restaurantCurrency }}
                    </v-chip>
                  </v-chip-group>
                </v-col>

                <!-- Submit Button -->
                <v-col cols="12" v-if="valid">
                  <primary-button
                    @click="submitTopup"
                    :disabled="submitting"
                    :loading="submitting"
                    block
                    :dark="valid && !submitting"
                    color="success"
                  >
                    <v-icon left>mdi-plus-circle</v-icon>
                    {{ $t("add_credit") }}
                  </primary-button>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import hasLimitOptions from "@/mixins/hasLimitOptions";
import employeeService from "@/services/employee";
import axios from "@/plugins/axios";

export default {
  name: "CompanyMultiRestaurantTabTopup",
  mixins: [hasSnackbar, hasLimitOptions],
  props: {
    preselectedUserId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      valid: false,
      submitting: false,
      topupAmount: null,
      paymentMethod: null,
      toppedBy: null,
      selectedLimit: null,
      selectedUsers: [],
      quickAmounts: [10, 20, 50, 100, 200],
      selectedQuickAmount: null,
      users: [],
    };
  },
  mounted() {
    this.fetchUsers();
    this.fetchLimits();
  },
  computed: {
    companyId() {
      return this.$store.state.company?.id;
    },
    companyRestaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    paymentMethods() {
      return [
        { label: this.$t("cash"), value: "cash" },
        { label: this.$t("card"), value: "card" },
        { label: this.$t("bank_transfer"), value: "transfer" },
      ];
    },
    toppedByOptions() {
      return [
        { label: "Firma", value: "Firma" },
        { label: "Radnik", value: "Radnik" },
      ];
    },
    amountRules() {
      return [
        (v) => !!v || this.$t("required_field"),
        (v) => v > 0 || this.$t("amount_must_be_positive"),
        (v) =>
          v <= 1000 ||
          this.$t("max_amount_exceeded", { currency: this.restaurantCurrency }),
      ];
    },
  },
  watch: {
    preselectedUserId: {
      handler(newVal) {
        if (newVal && this.users.length > 0) {
          this.selectedUsers = [newVal];
        }
      },
      immediate: false,
    },
  },
  methods: {
    async fetchUsers() {
      if (!this.companyId) return;
      try {
        const { data } = await employeeService.getCompanyUsers(this.companyId);
        this.users = (data.users || []).map((u) => ({
          id: u.id,
          name:
            u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username,
        }));
        if (this.preselectedUserId) this.selectedUsers = [this.preselectedUserId];
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_data"));
      }
    },
    setQuickAmount(amount) {
      this.topupAmount = amount;
    },
    formatCurrency(amount) {
      return (parseFloat(amount) || 0).toFixed(2);
    },
    async submitTopup() {
      if (!this.$refs.form.validate()) return;

      this.submitting = true;
      try {
        await Promise.all(
          this.selectedUsers.map((userId) =>
            axios.post("/limits/transactions", {
              quantity: 1,
              price: parseFloat(this.topupAmount),
              notes: "",
              user_id: userId,
              company_id: this.selectedLimit?.company_id,
              restaurant_id: this.selectedLimit?.restaurant_id,
              limit_id: this.selectedLimit?.id,
              date: new Date().toISOString(),
              limit_type: "credit",
              shift: "1-smena",
            })
          )
        );
        this.showSuccessSnackbar(this.$t("credit_added_successfully"));
        this.resetForm();
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_adding_credit"));
      } finally {
        this.submitting = false;
      }
    },
    resetForm() {
      this.topupAmount = null;
      this.paymentMethod = null;
      this.toppedBy = null;
      this.selectedLimit = null;
      this.selectedUsers = [];
      this.selectedQuickAmount = null;
      this.$refs.form?.resetValidation();
    },
  },
};
</script>

<style scoped>
.opacity-20 {
  opacity: 0.2;
}
</style>
